import React, { useState, useEffect } from "react";
import { useContext } from "react";
import { DataContext } from "../../providers/DataProvider";
import { useParams } from "react-router-dom";
import PieChartStats from "./statistics/graphs/PieChartStats";
import LineChartStats from "./statistics/graphs/LineChartStats";
import BarChartStats from "./statistics/graphs/BarChartStats";

function StatisticsPage() {
  const {
    handlePopulateEachEventSeries,
    eachEventSerieData,
    numbOfSignups,
    listOfUserSubmissions,
    getAllSubmissionsForEachEvent,
  } = useContext(DataContext);
  const { eventseriesid } = useParams();

  const [submissionsInsideEvent, setSubmissionsInsideEvent] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });

  const monthlyGroupedSubmissions = () => {
    const monthlySub = submissionsInsideEvent.flatMap(
      (event) => event.submissions.submissions || []
    );
    // Group by month
    const groupedByMonth = monthlySub.reduce((acc, submission) => {
      const date = new Date(submission.submitted_at);
      const month = date.toLocaleString("default", {
        month: "numeric",
      });
      const year = date.getFullYear();
      const monthYear = `${month} / ${year}`;
      if (!acc[monthYear]) {
        acc[monthYear] = 0;
      }
      acc[monthYear]++;
      return acc;
    }, {});
    // Convert to array of objects
    const monthlySubmissions = Object.entries(groupedByMonth).map(
      ([monthYear, count]) => ({
        monthYear,
        count,
      })
    );

    // Sort monthlySubmissions by date
    let monthlySubmissionsByDate = monthlySubmissions
      .filter((submission) => {
        return (
          !submission.monthYear.includes("Invalid Date") &&
          !submission.monthYear.includes("NaN")
        );
      })
      .sort((a, b) => {
        const dateA = new Date(a.monthYear.split(" / ").reverse().join("-"));
        const dateB = new Date(b.monthYear.split(" / ").reverse().join("-"));
        return dateB - dateA;
      });

    // Format monthYear to "MM / YYYY"
    monthlySubmissionsByDate.forEach((submission) => {
      const [month, year] = submission.monthYear.split(" / ");
      submission.monthYear = `${month.padStart(2, "0")} / ${year}`;
    });

    //console.log("monthlySubmissionsByDate", monthlySubmissionsByDate);

    return monthlySubmissionsByDate;
  };
  //console.log("monthlySub", monthlyGroupedSubmissions());

  useEffect(() => {
    handlePopulateEachEventSeries(eventseriesid);
  }, [eventseriesid]);

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        // Loop through the events object inside eachEventSerieData
        const submissions = await Promise.all(
          eachEventSerieData?.events?.map(async (event) => {
            const eventId = event.id;
            const submissions = await getAllSubmissionsForEachEvent(
              eventseriesid,
              eventId
            );
            return { eventId, submissions };
          })
        );

        setSubmissionsInsideEvent(submissions);
      } catch (error) {
        console.error("Error fetching submissions:", error);
      }
    };

    fetchSubmissions();
  }, [eachEventSerieData, eventseriesid]);

  return (
    <div className="flex flex-col items-center p-4   bg-gray-100">
      <div className="justify-center items-center text-center mb-8">
        <h1 className="text-4xl font-bold mb-4">Statistics</h1>
        <p className="text-lg text-gray-700 mb-8">
          Here you can view the statistics of your events.
        </p>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2   gap-4 ">
        <div className="bg-white shadow-md rounded-lg p-6  ">
          <h2 className="text-2xl font-semibold mb-4">Events Statistics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-100 p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-blue-600">
                {eachEventSerieData?.events?.length}
              </p>
              <p className="text-gray-600">Total events</p>
            </div>

            <div className="bg-amber-100 p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-amber-600">
                {
                  eachEventSerieData?.events?.filter(
                    (event) => event.show_on_form === true
                  ).length
                }
              </p>
              <p className="text-gray-600">On going events</p>
            </div>
            <div className="bg-green-100 p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-green-600">
                {
                  eachEventSerieData?.events?.filter(
                    (event) => event.show_on_form === false
                  ).length
                }
              </p>
              <p className="text-gray-600">Completed events</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 mb-4">
            <h2 className="text-2xl font-semibold mb-4">Submissions</h2>
            <div className="flex flex-col w-full">
              <div className="flex flex-row bg-blue-50 border border-blue-200">
                <div className="flex-1 border-blue-200 px-3 py-6">Date</div>
                <div className="flex-1 border-blue-200 px-3 py-6">
                  Numb of signups
                </div>
              </div>

              {monthlyGroupedSubmissions()?.map((submission, index) => {
                // Calculate difference compared to the next month
                const nextSubmissionCount = monthlyGroupedSubmissions()[
                  index + 1
                ]
                  ? monthlyGroupedSubmissions()[index + 1].count
                  : 0;

                // Calculate the difference
                const difference = submission.count - nextSubmissionCount;

                

                return (
                  <div
                    key={index}
                    className="flex flex-row border-b border-blue-200"
                  >
                    <div className="flex-1 border-blue-200 px-3 py-6">
                      {submission.monthYear}
                    </div>
                    <div className="flex gap-4 flex-1 border-blue-200 px-3 py-6">
                      {submission?.count || 0}
                      <div className="flex gap-2">
                        {difference === submission.count ? (
                          <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 font-semibold">
                            No change
                          </span>
                        ) : difference > 0 ? (
                          <span className="inline-flex items-center rounded-md bg-green-200 px-2 py-1 text-xs font-medium text-green-600  ring-1 font-semibold ">
                            <p>{`+${difference}`}</p>
                          </span>
                        ) : difference < 0 ? (
                          <span className="inline-flex items-center rounded-md bg-green-200 px-2 py-1 text-xs font-medium text-red-600 font-semibold">
                            <p>{`-${difference}`}</p>
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6  ">
          <div className="bg-white  rounded-lg ">
            <h2 className="text-2xl font-semibold mb-4">This month's vs last month signups</h2>

            <div className="m-4 ">
              <div className="bg-blue-100 p-4 rounded-lg text-center  ">
                <BarChartStats
                  eventSeriesData={eachEventSerieData}
                  submissionsInsideEvent={submissionsInsideEvent}
                  dateRange={dateRange}
                />
              </div>
            </div>
            <h2 className="text-2xl font-semibold mb-4">Select Date Range</h2>

            <div className="bg-blue-100 p-4 rounded-lg text-center  ">
              
              <input
                value={dateRange.startDate}
                onChange={(e) =>
                  setDateRange({ ...dateRange, startDate: e.target.value })
                }
                type="date"
                className="w-full bg-white border border-gray-300 rounded-lg p-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
              />
              <input
                value={dateRange.endDate}
                onChange={(e) =>
                  setDateRange({ ...dateRange, endDate: e.target.value })
                }
                type="date"
                className="w-full bg-white border border-gray-300 rounded-lg p-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="m-4 ">
              <div className="bg-blue-100 p-4 rounded-lg text-center  ">
                <LineChartStats
                  eventSeriesData={eachEventSerieData}
                  submissionsInsideEvent={submissionsInsideEvent}
                  dateRange={dateRange}
                />
              </div>
            </div>
             
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatisticsPage;
